import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  List,
  ListItem,
  ListItemText,
  useTheme
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import SearchIcon from '@mui/icons-material/Search';
import GitHubIcon from '@mui/icons-material/GitHub';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DiffEditor } from '@monaco-editor/react';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';

// -------------------------- DUMMY DATA --------------------------
const dummyFileList = [
  {
    id: "folder-1",
    name: "src",
    type: "folder",
    children: [
      { id: "file-1", name: "main.cpp", type: "file", path: "src/main.cpp" },
      { id: "file-2", name: "utils.cpp", type: "file", path: "src/utils.cpp" }
    ]
  },
  {
    id: "folder-2",
    name: "lib",
    type: "folder",
    children: [
      { id: "file-3", name: "LedBlink.cpp", type: "file", path: "lib/LedBlink.cpp" }
    ]
  },
  { id: "file-4", name: "README.md", type: "file", path: "README.md" }
];

const codeDiffData = {
  "src/main.cpp": {
    original: `#include <stdio.h>
int main() {
    printf("Hello, Arduino!\\n");
    return 0;
}`,
    modified: `#include <stdio.h>
#define GREETING "Hello, Arduino!"
int main() {
    printf(GREETING);
    return 0;
}`
  },
  "src/utils.cpp": {
    original: `// Utility functions
int add(int a, int b) {
    return a+b;
}`,
    modified: `// Utility functions
int add(int a, int b) {
    // Added error checking
    return a + b;
}`
  },
  "lib/LedBlink.cpp": {
    original: `#include <Arduino.h>
void setup() {
    pinMode(13, OUTPUT);
}
void loop() {
    digitalWrite(13, HIGH);
    delay(1000);
    digitalWrite(13, LOW);
    delay(1000);
}`,
    modified: `#include <Arduino.h>
#define BLINK_DELAY 1000
void setup() {
    pinMode(13, OUTPUT);
}
void loop() {
    digitalWrite(13, HIGH);
    delay(BLINK_DELAY);
    digitalWrite(13, LOW);
    delay(BLINK_DELAY);
}`
  },
  "README.md": {
    original: `# LED Blink Project
This is a simple LED blink project.`,
    modified: `# LED Blink Project
This is a simple LED blink project with improvements.`
  }
};

const issuesData = [
  {
    id: 1,
    description: "Magic number used",
    beforeLine: 4,
    afterLine: 5,
    beforeMessage: "MISRA C violation: Magic number used.",
    afterMessage: "Replaced magic number with a defined constant."
  },
  {
    id: 2,
    description: "Missing null check",
    beforeLine: 6,
    afterLine: 7,
    beforeMessage: "MISRA C violation: Missing null pointer check.",
    afterMessage: "Added null pointer check."
  }
];

// -------------------------- CODE DIFF COMPONENT --------------------------
const CodeDiffWithHover = ({ originalCode, modifiedCode, issues, isDarkMode }) => {
  const handleEditorDidMount = (editor, monaco) => {
    const originalEditor = editor.getOriginalEditor();
    const modifiedEditor = editor.getModifiedEditor();

    const beforeDecorations = issues.map(issue => ({
      range: new monaco.Range(issue.beforeLine, 1, issue.beforeLine, 1),
      options: {
        isWholeLine: true,
        className: 'beforeLineHighlight',
        hoverMessage: { value: issue.beforeMessage }
      }
    }));

    const afterDecorations = issues.map(issue => ({
      range: new monaco.Range(issue.afterLine, 1, issue.afterLine, 1),
      options: {
        isWholeLine: true,
        className: 'afterLineHighlight',
        hoverMessage: { value: issue.afterMessage }
      }
    }));

    originalEditor.deltaDecorations([], beforeDecorations);
    modifiedEditor.deltaDecorations([], afterDecorations);
  };

  return (
    <Paper 
      elevation={4} 
      sx={{ 
        height: '85vh', 
        borderRadius: 2, 
        overflow: 'hidden',
        bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff'
      }}
    >
      <DiffEditor
        original={originalCode}
        modified={modifiedCode}
        language="cpp"
        onMount={handleEditorDidMount}
        options={{
          renderSideBySide: true,
          ignoreTrimWhitespace: true,
          automaticLayout: true
        }}
        theme={isDarkMode ? "vs-dark" : "light"}
        height={'100%'}
      />
    </Paper>
  );
};

// -------------------------- FILE TREE (Explorer) --------------------------
const FileTree = ({ fileTree = [], onFileSelect, isDarkMode }) => {
  const renderTreeItems = (items) => {
    return items.map((item) => (
      <TreeItem
        key={item.id}
        itemId={item.id}
        label={item.name}
        onClick={() => {
          if (item.type === 'file') {
            onFileSelect(item);
          }
        }}
        sx={{
          '& .MuiTreeItem-content': {
            color: isDarkMode ? '#cccccc' : '#333333',
            '&:hover': {
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)'
            },
            '&.Mui-selected': {
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.08)'
            }
          }
        }}
      >
        {item.children && renderTreeItems(item.children)}
      </TreeItem>
    ));
  };

  return (
    <SimpleTreeView>
      {renderTreeItems(fileTree)}
    </SimpleTreeView>
  );
};

const ExplorerPanel = ({ fileTree, onFileSelect, isDarkMode }) => (
  <Box sx={{ p: 2 }}>
    <Typography 
      variant="h6" 
      sx={{ 
        color: isDarkMode ? '#ffffff' : '#000000',
        mb: 2
      }}
    >
      Explorer
    </Typography>
    <FileTree fileTree={fileTree} onFileSelect={onFileSelect} isDarkMode={isDarkMode} />
  </Box>
);

const SourceControlPanel = ({ isDarkMode }) => (
  <Box sx={{ p: 2 }}>
    <Typography 
      variant="h6" 
      sx={{ 
        color: isDarkMode ? '#ffffff' : '#000000',
        mb: 2
      }}
    >
      Source Control
    </Typography>
    <Typography 
      variant="body2" 
      sx={{ 
        color: isDarkMode ? '#cccccc' : '#666666',
        mb: 2
      }}
    >
      Dummy Git version control panel.
    </Typography>
    <List>
      <ListItem 
        sx={{ 
          borderRadius: 1,
          '&:hover': {
            bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <ListItemText 
          primary="src/main.cpp modified" 
          sx={{ 
            color: isDarkMode ? '#4fc1ff' : '#0066cc'
          }} 
        />
      </ListItem>
      <ListItem 
        sx={{ 
          borderRadius: 1,
          '&:hover': {
            bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <ListItemText 
          primary="lib/LedBlink.cpp added" 
          sx={{ 
            color: isDarkMode ? '#89d185' : '#28a745'
          }} 
        />
      </ListItem>
    </List>
  </Box>
);

const SearchPanel = ({ isDarkMode }) => (
  <Box sx={{ p: 2 }}>
    <Typography 
      variant="h6" 
      sx={{ 
        color: isDarkMode ? '#ffffff' : '#000000',
        mb: 2
      }}
    >
      Search
    </Typography>
    <TextField 
      fullWidth 
      variant="outlined" 
      placeholder="Search files..." 
      sx={{
        mb: 2,
        '& .MuiOutlinedInput-root': {
          color: isDarkMode ? '#ffffff' : '#000000',
          '& fieldset': {
            borderColor: isDarkMode ? '#555555' : '#cccccc'
          },
          '&:hover fieldset': {
            borderColor: isDarkMode ? '#777777' : '#999999'
          },
          '&.Mui-focused fieldset': {
            borderColor: isDarkMode ? '#0066cc' : '#0066cc'
          }
        },
        '& .MuiInputBase-input::placeholder': {
          color: isDarkMode ? '#999999' : '#666666',
          opacity: 1
        }
      }}
    />
    <List>
      <ListItem 
        sx={{ 
          borderRadius: 1,
          '&:hover': {
            bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <ListItemText 
          primary="Result 1" 
          sx={{ 
            color: isDarkMode ? '#cccccc' : '#333333'
          }} 
        />
      </ListItem>
      <ListItem 
        sx={{ 
          borderRadius: 1,
          '&:hover': {
            bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <ListItemText 
          primary="Result 2" 
          sx={{ 
            color: isDarkMode ? '#cccccc' : '#333333'
          }} 
        />
      </ListItem>
    </List>
  </Box>
);

// -------------------------- EXTENSIONS SIDEBARS --------------------------
const ExtensionsBar = ({ selectedExtension, onSelectExtension, controlsAlignment, onToggleAlignment, isDarkMode }) => {
  const icons = [
    { key: 'explorer', icon: <FolderIcon /> },
    { key: 'search', icon: <SearchIcon /> },
    { key: 'source', icon: <GitHubIcon /> }
  ];

  return (
    <Box
      sx={{
        width: 60,
        bgcolor: isDarkMode ? '#252526' : '#f3f3f3',
        color: isDarkMode ? '#cccccc' : '#333333',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 1,
        height: '100%',
        ...(controlsAlignment === 'right'
          ? { borderLeft: `1px solid ${isDarkMode ? '#444' : '#ddd'}` }
          : { borderRight: `1px solid ${isDarkMode ? '#444' : '#ddd'}` })
      }}
    >
      <Box sx={{ flex: '0 0 auto' }}>
        {icons.map(({ key, icon }) => (
          <IconButton
            key={key}
            color={selectedExtension === key ? 'primary' : 'default'}
            onClick={() => onSelectExtension(key)}
            sx={{ 
              mb: 1,
              color: selectedExtension === key 
                ? '#0066cc' 
                : (isDarkMode ? '#cccccc' : '#666666'),
              '&:hover': {
                bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            {icon}
          </IconButton>
        ))}
      </Box>
      
      <Box sx={{ flexGrow: 1 }} />
      
      <Box sx={{ 
        flex: '0 0 auto',
        borderTop: `1px solid ${isDarkMode ? '#444' : '#ddd'}`,
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        pt: 1
      }}>
        <IconButton 
          onClick={onToggleAlignment} 
          sx={{ 
            color: isDarkMode ? '#cccccc' : '#666666',
            '&:hover': {
              bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)'
            }
          }}
          title={controlsAlignment === 'right' ? 'Move sidebar to left' : 'Move sidebar to right'}
        >
          {controlsAlignment === 'right' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>
    </Box>
  );
};

const ExtensionPanel = ({ selectedExtension, fileTree, onFileSelect, controlsAlignment, isDarkMode }) => {
  let content = null;
  if (selectedExtension === 'explorer') {
    content = <ExplorerPanel fileTree={fileTree} onFileSelect={onFileSelect} isDarkMode={isDarkMode} />;
  } else if (selectedExtension === 'search') {
    content = <SearchPanel isDarkMode={isDarkMode} />;
  } else if (selectedExtension === 'source') {
    content = <SourceControlPanel isDarkMode={isDarkMode} />;
  }
  return (
    <Box
      sx={{
        width: 300,
        bgcolor: isDarkMode ? '#1e1e1e' : '#f8f8f8',
        ...(controlsAlignment === 'right'
          ? { borderLeft: `1px solid ${isDarkMode ? '#444' : '#ddd'}` }
          : { borderRight: `1px solid ${isDarkMode ? '#444' : '#ddd'}` }),
        overflow: 'auto',
        color: isDarkMode ? '#cccccc' : '#333333'
      }}
    >
      {content}
    </Box>
  );
};

// -------------------------- MAIN APP --------------------------
const CodeDiffViewer = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedExtension, setSelectedExtension] = useState(null);
  const storedAlignment = localStorage.getItem('controlsAlignment') || 'right';
  const [controlsAlignment, setControlsAlignment] = useState(storedAlignment);

  const handleSelectExtension = (ext) => {
    setSelectedExtension(selectedExtension === ext ? null : ext);
  };

  const handleToggleAlignment = () => {
    const newAlignment = controlsAlignment === 'right' ? 'left' : 'right';
    setControlsAlignment(newAlignment);
    localStorage.setItem('controlsAlignment', newAlignment);
  };

  const currentCodeDiff =
    selectedFile && codeDiffData[selectedFile.path]
      ? codeDiffData[selectedFile.path]
      : null;

  return (
    <Box
      sx={{
        display: 'flex',
        height: `calc(100vh - 64px)`,
        bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
        borderTop: `2px solid ${isDarkMode ? '#444' : '#ddd'}`
      }}
    >
      {controlsAlignment === 'left' && (
        <>
          <ExtensionsBar
            selectedExtension={selectedExtension}
            onSelectExtension={handleSelectExtension}
            controlsAlignment={controlsAlignment}
            onToggleAlignment={handleToggleAlignment}
            isDarkMode={isDarkMode}
          />
          {selectedExtension && (
            <ExtensionPanel
              selectedExtension={selectedExtension}
              fileTree={dummyFileList}
              onFileSelect={setSelectedFile}
              controlsAlignment={controlsAlignment}
              isDarkMode={isDarkMode}
            />
          )}
        </>
      )}

      <Box sx={{ flexGrow: 1, p: 2 }}>
        {selectedFile && currentCodeDiff ? (
          <Box>
            <Typography 
              variant="h5" 
              sx={{ 
                color: isDarkMode ? '#ffffff' : '#000000',
                fontSize: '20px',
                mb: 2
              }} 
              gutterBottom
            >
              {selectedFile.path}
            </Typography>
            <CodeDiffWithHover
              originalCode={currentCodeDiff.original}
              modifiedCode={currentCodeDiff.modified}
              issues={issuesData}
              isDarkMode={isDarkMode}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                color: isDarkMode ? '#888888' : '#666666'
              }}
            >
              Select a file from the Explorer to view code diff.
            </Typography>
          </Box>
        )}
      </Box>

      {controlsAlignment === 'right' && (
        <>
          {selectedExtension && (
            <ExtensionPanel
              selectedExtension={selectedExtension}
              fileTree={dummyFileList}
              onFileSelect={setSelectedFile}
              controlsAlignment={controlsAlignment}
              isDarkMode={isDarkMode}
            />
          )}
          <ExtensionsBar
            selectedExtension={selectedExtension}
            onSelectExtension={handleSelectExtension}
            controlsAlignment={controlsAlignment}
            onToggleAlignment={handleToggleAlignment}
            isDarkMode={isDarkMode}
          />
        </>
      )}
    </Box>
  );
};

export default CodeDiffViewer;